import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { LoadingSpinner } from './components/LoadingSpinner';
import { SessionExpiredModal } from './components/SessionExpiredModal';
import { resetSessionExpiredFlag } from './config/axios';
import './App.css';

// Eagerly loaded components (critical path)
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { AccessDenied } from './pages/AccessDenied';
import { PermissionGuard } from './guards/PermissionGuard';
import { GlobalAdminGuard } from './guards/GlobalAdminGuard';
import { PagePermissionLevel } from './hooks/usePermissions';

/**
 * Track if app has finished initial auth initialization
 * This is used to differentiate between:
 * - Initial load (first render after navigation) → silent redirect
 * - Mid-session expiration (user already inside the app) → show modal
 */
let hasCompletedInitialAuth = false;

// Lazy loaded components (code split)
const AgentsList = lazy(() => import('./pages/agents/AgentsList').then(m => ({ default: m.AgentsList })));
const AgentCreate = lazy(() => import('./pages/agents/AgentCreate').then(m => ({ default: m.AgentCreate })));
const AgentEdit = lazy(() => import('./pages/agents/AgentEdit').then(m => ({ default: m.AgentEdit })));
const AgentPage = lazy(() => import('./pages/agents/AgentPage').then(m => ({ default: m.AgentPage })));

const OutboundDialersList = lazy(() => import('./pages/outbound-dialers/OutboundDialersList').then(m => ({ default: m.OutboundDialersList })));
const OutboundDialerCreate = lazy(() => import('./pages/outbound-dialers/OutboundDialerCreate').then(m => ({ default: m.OutboundDialerCreate })));
const OutboundDialerView = lazy(() => import('./pages/outbound-dialers/OutboundDialerView').then(m => ({ default: m.OutboundDialerView })));
const OutboundDialerEdit = lazy(() => import('./pages/outbound-dialers/OutboundDialerEdit').then(m => ({ default: m.OutboundDialerEdit })));

const ToolsList = lazy(() => import('./pages/tools/ToolsList').then(m => ({ default: m.ToolsList })));
const ToolCreate = lazy(() => import('./pages/tools/ToolCreate').then(m => ({ default: m.ToolCreate })));
const ToolEdit = lazy(() => import('./pages/tools/ToolEdit').then(m => ({ default: m.ToolEdit })));
const WebhookTester = lazy(() => import('./pages/tools/WebhookTester').then(m => ({ default: m.WebhookTester })));
const ToolTester = lazy(() => import('./pages/tools/ToolTester').then(m => ({ default: m.ToolTester })));

const KnowledgeBasesList = lazy(() => import('./pages/knowledge-bases/KnowledgeBasesList').then(m => ({ default: m.KnowledgeBasesList })));
const KnowledgeBaseCreate = lazy(() => import('./pages/knowledge-bases/KnowledgeBaseCreate').then(m => ({ default: m.KnowledgeBaseCreate })));
const KnowledgeBaseEdit = lazy(() => import('./pages/knowledge-bases/KnowledgeBaseEdit').then(m => ({ default: m.KnowledgeBaseEdit })));
const KnowledgeBaseProcessing = lazy(() => import('./pages/knowledge-bases/KnowledgeBaseProcessing').then(m => ({ default: m.KnowledgeBaseProcessing })));

const VocabularyList = lazy(() => import('./pages/vocabulary/VocabularyList').then(m => ({ default: m.VocabularyList })));
const VocabularyBookEdit = lazy(() => import('./pages/vocabulary/VocabularyBookEdit').then(m => ({ default: m.VocabularyBookEdit })));

const AgentGroups = lazy(() => import('./pages/groups/AgentGroups').then(m => ({ default: m.AgentGroups })));
const AgentGroupCreate = lazy(() => import('./pages/groups/AgentGroupCreate').then(m => ({ default: m.AgentGroupCreate })));
const AgentGroupEdit = lazy(() => import('./pages/groups/AgentGroupEdit').then(m => ({ default: m.AgentGroupEdit })));
const ToolGroups = lazy(() => import('./pages/groups/ToolGroups').then(m => ({ default: m.ToolGroups })));
const ToolGroupCreate = lazy(() => import('./pages/groups/ToolGroupCreate').then(m => ({ default: m.ToolGroupCreate })));
const ToolGroupEdit = lazy(() => import('./pages/groups/ToolGroupEdit').then(m => ({ default: m.ToolGroupEdit })));

const MediaServers = lazy(() => import('./pages/infrastructure/MediaServers').then(m => ({ default: m.MediaServers })));
const MediaServerCreate = lazy(() => import('./pages/infrastructure/MediaServerCreate').then(m => ({ default: m.MediaServerCreate })));
const MediaServerEdit = lazy(() => import('./pages/infrastructure/MediaServerEdit').then(m => ({ default: m.MediaServerEdit })));
const ToolRunners = lazy(() => import('./pages/infrastructure/ToolRunners').then(m => ({ default: m.ToolRunners })));
const ToolRunnerCreate = lazy(() => import('./pages/infrastructure/ToolRunnerCreate').then(m => ({ default: m.ToolRunnerCreate })));
const ToolRunnerEdit = lazy(() => import('./pages/infrastructure/ToolRunnerEdit').then(m => ({ default: m.ToolRunnerEdit })));
const VirtualInterfaces = lazy(() => import('./pages/infrastructure/VirtualInterfaces').then(m => ({ default: m.VirtualInterfaces })));
const VirtualInterfaceCreate = lazy(() => import('./pages/infrastructure/VirtualInterfaceCreate').then(m => ({ default: m.VirtualInterfaceCreate })));
const VirtualInterfaceEdit = lazy(() => import('./pages/infrastructure/VirtualInterfaceEdit').then(m => ({ default: m.VirtualInterfaceEdit })));

// Email Templates
const EmailTemplatesList = lazy(() => import('./pages/email-templates/EmailTemplatesList'));
const EmailTemplateCreate = lazy(() => import('./pages/email-templates/EmailTemplateCreate'));
const EmailTemplateEdit = lazy(() => import('./pages/email-templates/EmailTemplateEdit'));

const TenantsList = lazy(() => import('./pages/admin/TenantsList').then(m => ({ default: m.TenantsList })));
const TenantCreate = lazy(() => import('./pages/admin/TenantCreate').then(m => ({ default: m.TenantCreate })));
const TenantEdit = lazy(() => import('./pages/admin/TenantEdit').then(m => ({ default: m.TenantEdit })));
const AuditLogs = lazy(() => import('./pages/admin/AuditLogs').then(m => ({ default: m.AuditLogs })));

const CallLogs = lazy(() => import('./pages/analytics/CallLogs').then(m => ({ default: m.CallLogs })));
const ToolLogs = lazy(() => import('./pages/analytics/ToolLogs').then(m => ({ default: m.ToolLogs })));

const AdministratorsList = lazy(() => import('./pages/administrators/AdministratorsList').then(m => ({ default: m.AdministratorsList })));
const CreateAdministrator = lazy(() => import('./pages/administrators/CreateAdministrator').then(m => ({ default: m.CreateAdministrator })));
const EditAdministrator = lazy(() => import('./pages/administrators/EditAdministrator').then(m => ({ default: m.EditAdministrator })));

const Settings = lazy(() => import('./pages/settings/Settings').then(m => ({ default: m.Settings })));
const UpstreamProvidersPage = lazy(() => import('./pages/upstream-providers/UpstreamProvidersPage').then(m => ({ default: m.UpstreamProvidersPage })));

const BillingReport = lazy(() => import('./pages/billing/BillingReport').then(m => ({ default: m.BillingReport })));
const BillingRates = lazy(() => import('./pages/billing/BillingRates').then(m => ({ default: m.BillingRates })));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Mark initial auth as complete after first check
  useEffect(() => {
    if (!isLoading) {
      hasCompletedInitialAuth = true;
    }
  }, [isLoading]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
      {/* Public routes */}
      <Route
        path="/admin/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Agents */}
      <Route
        path="/admin/agents"
        element={
          <ProtectedRoute>
            <AgentsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents/create"
        element={
          <ProtectedRoute>
            <AgentCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents/:id/edit"
        element={
          <ProtectedRoute>
            <AgentEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents/:id"
        element={
          <ProtectedRoute>
            <AgentPage />
          </ProtectedRoute>
        }
      />

      {/* Outbound Dialers */}
      <Route
        path="/admin/outbound-dialers"
        element={
          <ProtectedRoute>
            <OutboundDialersList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outbound-dialers/create"
        element={
          <ProtectedRoute>
            <OutboundDialerCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outbound-dialers/:id"
        element={
          <ProtectedRoute>
            <OutboundDialerView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outbound-dialers/:id/edit"
        element={
          <ProtectedRoute>
            <OutboundDialerEdit />
          </ProtectedRoute>
        }
      />

      {/* Tools */}
      <Route
        path="/admin/tools"
        element={
          <ProtectedRoute>
            <ToolsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tools/create"
        element={
          <ProtectedRoute>
            <ToolCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tools/:id/edit"
        element={
          <ProtectedRoute>
            <ToolEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tools/webhook-tester"
        element={
          <ProtectedRoute>
            <WebhookTester />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tools/tester"
        element={
          <ProtectedRoute>
            <ToolTester />
          </ProtectedRoute>
        }
      />

      {/* Knowledge Bases */}
      <Route
        path="/admin/knowledge-bases"
        element={
          <ProtectedRoute>
            <KnowledgeBasesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/knowledge-bases/create"
        element={
          <ProtectedRoute>
            <KnowledgeBaseCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/knowledge-bases/:id"
        element={
          <ProtectedRoute>
            <KnowledgeBaseEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/knowledge-bases/:id/processing"
        element={
          <ProtectedRoute>
            <KnowledgeBaseProcessing />
          </ProtectedRoute>
        }
      />

      {/* Vocabulary Books */}
      <Route
        path="/admin/vocabulary"
        element={
          <ProtectedRoute>
            <VocabularyList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vocabulary/:bookId"
        element={
          <ProtectedRoute>
            <VocabularyBookEdit />
          </ProtectedRoute>
        }
      />

      {/* Groups */}
      <Route
        path="/admin/groups/agents"
        element={
          <ProtectedRoute>
            <AgentGroups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/groups/agents/create"
        element={
          <ProtectedRoute>
            <AgentGroupCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/groups/agents/edit/:groupId"
        element={
          <ProtectedRoute>
            <AgentGroupEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/groups/tools"
        element={
          <ProtectedRoute>
            <ToolGroups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/groups/tools/create"
        element={
          <ProtectedRoute>
            <ToolGroupCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/groups/tools/edit/:groupId"
        element={
          <ProtectedRoute>
            <ToolGroupEdit />
          </ProtectedRoute>
        }
      />
      {/* Infrastructure */}
      <Route
        path="/admin/media-servers"
        element={
          <ProtectedRoute>
            <MediaServers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/media-servers/create"
        element={
          <ProtectedRoute>
            <MediaServerCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/media-servers/:id/edit"
        element={
          <ProtectedRoute>
            <MediaServerEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tool-runners"
        element={
          <ProtectedRoute>
            <ToolRunners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tool-runners/create"
        element={
          <ProtectedRoute>
            <ToolRunnerCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tool-runners/:id/edit"
        element={
          <ProtectedRoute>
            <ToolRunnerEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/virtual-interfaces"
        element={
          <ProtectedRoute>
            <VirtualInterfaces />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/virtual-interfaces/create"
        element={
          <ProtectedRoute>
            <VirtualInterfaceCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/virtual-interfaces/:id/edit"
        element={
          <ProtectedRoute>
            <VirtualInterfaceEdit />
          </ProtectedRoute>
        }
      />

      {/* Administration */}
      <Route
        path="/admin/administrators"
        element={
          <ProtectedRoute>
            <PermissionGuard pageId="page_administrators" requiredLevel={PagePermissionLevel.VIEW}>
              <AdministratorsList />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/administrators/create"
        element={
          <ProtectedRoute>
            <PermissionGuard pageId="page_administrators" requiredLevel={PagePermissionLevel.EDIT}>
              <CreateAdministrator />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/administrators/:id/edit"
        element={
          <ProtectedRoute>
            <PermissionGuard pageId="page_administrators" requiredLevel={PagePermissionLevel.EDIT}>
              <EditAdministrator />
            </PermissionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <TenantsList />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants/create"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <TenantCreate />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants/:tenantId/edit"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <TenantEdit />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute>
            <AuditLogs />
          </ProtectedRoute>
        }
      />

      {/* Email Templates */}
      <Route
        path="/admin/email-templates"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <EmailTemplatesList />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-templates/create"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <EmailTemplateCreate />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-templates/:id/edit"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <EmailTemplateEdit />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />

      {/* Upstream Providers (Global Admin only) */}
      <Route
        path="/admin/upstream-providers"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <UpstreamProvidersPage />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Access Denied */}
      <Route
        path="/admin/access-denied"
        element={
          <ProtectedRoute>
            <AccessDenied />
          </ProtectedRoute>
        }
      />

      {/* Analytics */}
      <Route
        path="/admin/call-logs"
        element={
          <ProtectedRoute>
            <CallLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tool-logs"
        element={
          <ProtectedRoute>
            <ToolLogs />
          </ProtectedRoute>
        }
      />

      {/* Billing (Global Admin only) */}
      <Route
        path="/admin/billing-report"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <BillingReport />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing-rates"
        element={
          <ProtectedRoute>
            <GlobalAdminGuard>
              <BillingRates />
            </GlobalAdminGuard>
          </ProtectedRoute>
        }
      />

      {/* Default redirects */}
      <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function AppContent() {
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const { isLoading } = useAuth();

  useEffect(() => {
    // Listen for session-expired events from axios interceptor
    const handleSessionExpired = () => {
      // Only show modal if we've completed initial auth (mid-session expiration)
      // If initial auth hasn't completed, let the protected route redirect silently
      if (hasCompletedInitialAuth && !isLoading) {
        setShowSessionExpired(true);
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [isLoading]);

  const handleCloseModal = () => {
    setShowSessionExpired(false);
    resetSessionExpiredFlag();
  };

  return (
    <>
      <AppRoutes />
      <SessionExpiredModal isOpen={showSessionExpired} onClose={handleCloseModal} />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <AppContent />
      </TenantProvider>
    </AuthProvider>
  );
}

export default App;
